import { useState } from "react";
import {
  Badge,
  Group,
  Modal,
  Button,
  Stack,
  Checkbox,
  SimpleGrid,
  ScrollArea,
  Box,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconEdit } from "@tabler/icons-react";
import { useTaskStore } from "../../store/task.store";
import { userPreferencesService } from "../../services/userPreferences.service";

export function TaskSwitcher() {
  const { permittedTasks, selectedTasks, setSelectedTasks } = useTaskStore();
  const [opened, { open, close }] = useDisclosure(false);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  const canSwitch = permittedTasks.length > 1;

  if (!canSwitch) {
    return (
      <Group gap={4}>
        {selectedTasks.map((t) => (
          <Badge key={t.id} color="gcpBlue" variant="light" size="sm">
            {t.name}
          </Badge>
        ))}
      </Group>
    );
  }

  const handleOpen = () => {
    setDraft(new Set(selectedTasks.map((t) => t.id)));
    open();
  };

  const toggle = (id: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleApply = async () => {
    if (draft.size === 0) {
      notifications.show({
        title: "Select at least one task",
        message: "You must have at least one task selected.",
        color: "orange",
      });
      return;
    }
    const tasks = permittedTasks.filter((t) => draft.has(t.id));
    setSelectedTasks(tasks);
    setIsSaving(true);
    try {
      await userPreferencesService.saveSelectedTasks(tasks.map((t) => t.id));
    } catch (error) {
      notifications.show({
        title: "Could not save preference",
        message: "Your selection is active for this session.",
        color: "orange",
      });
    } finally {
      setIsSaving(false);
    }
    close();
  };

  return (
    <>
      <UnstyledButton
        onClick={handleOpen}
        style={{
          cursor: "pointer",
          padding: "4px 10px",
          borderRadius: "var(--mantine-radius-sm)",
          border: "1px dashed var(--mantine-color-gcpBlue-3)",
          transition: "background-color 0.15s, border-color 0.15s",
        }}
        styles={{
          root: {
            "&:hover": {
              backgroundColor: "var(--mantine-color-gcpBlue-0)",
              borderColor: "var(--mantine-color-gcpBlue-5)",
            },
          },
        }}
      >
        <Group gap={6} wrap="nowrap">
          {selectedTasks.map((t) => (
            <Badge key={t.id} color="gcpBlue" variant="light" size="sm">
              {t.name}
            </Badge>
          ))}
          <IconEdit
            size={14}
            style={{ color: "var(--mantine-color-gcpBlue-5)", flexShrink: 0 }}
          />
        </Group>
      </UnstyledButton>

      <Modal
        opened={opened}
        onClose={close}
        title="Switch Tasks"
        size="md"
        closeOnClickOutside={false}
        styles={{ body: { padding: 0 } }}
      >
        <ScrollArea.Autosize
          mah="calc(100vh - 300px)"
          offsetScrollbars
          px="md"
          pt="md"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Select the tasks you want to work on. Navigation updates
              instantly.
            </Text>
            <SimpleGrid cols={2}>
              {permittedTasks.map((task) => (
                <Checkbox
                  key={task.id}
                  label={task.name}
                  checked={draft.has(task.id)}
                  onChange={() => toggle(task.id)}
                  color="gcpBlue"
                />
              ))}
            </SimpleGrid>
          </Stack>
        </ScrollArea.Autosize>

        <Box
          px="md"
          py="sm"
          style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
        >
          <Group justify="space-between">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button
              color="gcpBlue"
              onClick={handleApply}
              disabled={draft.size === 0 || isSaving}
              loading={isSaving}
            >
              Apply
            </Button>
          </Group>
        </Box>
      </Modal>
    </>
  );
}
